import React from 'react';

const PrefilledQuestionStep = ({ question, value, onAnswer, onNext, userData }) => {
  // Get the prefilled value from userData if available
  const prefilledValue = userData?.[question.fieldMapping] || '';
  const currentValue = value || prefilledValue;

  // PrefilledQuestionStep owns the auto-advance for prefilled single-selects:
  // it fires on every click (whether the user confirms the prefilled value or
  // changes it). FollowUpForm's handleAnswer auto-advance is gated to
  // `answer !== previousAnswer`, which silently no-ops when the click matches
  // prefill (e.g., user confirms a prefilled Male) — leaving the slide stuck.
  // To avoid the double-advance that previously skipped height-weight
  // (FE_PRD_SINGLE_SELECT_AUTOADVANCE_RACE), FollowUpForm.handleAnswer
  // intentionally excludes 'single-select-prefilled' from its auto-advance.
  const handleOptionClick = (optionValue) => {
    onAnswer(optionValue);
    setTimeout(() => {
      onNext();
    }, 200);
  };
  
  return (
    <div data-testid={`step-${question.id}`}>
      {/* Question */}
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">
        {question.question}
      </h2>
      
      {/* Pre-filled indicator */}
      {prefilledValue && (
        <div className="flex items-center justify-center gap-2 mb-6">
          <svg className="w-4 h-4 text-[#2d8a5f]" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
          <span className="text-sm text-[#2d8a5f]">Pre-filled from your records</span>
        </div>
      )}
      
      {/* Subtitle if exists */}
      {question.subtitle && (
        <p className="text-center text-gray-500 mb-6 -mt-2">
          {question.subtitle}
        </p>
      )}
      
      {/* Options */}
      <div className="space-y-3">
        {question.options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleOptionClick(option.value || option)}
            className={`option-card w-full p-5 rounded-xl text-left transition-all ${
              currentValue === (option.value || option) ? 'selected' : ''
            }`}
            data-testid={`option-${index}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-base font-medium text-gray-800">
                  {option.label || option}
                </span>
              </div>
              {/* Radio button indicator */}
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                currentValue === (option.value || option)
                  ? 'border-[#2d8a5f] bg-[#2d8a5f]'
                  : 'border-gray-300'
              }`}>
                {currentValue === (option.value || option) && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </div>
            {option.description && (
              <p className="text-sm text-gray-500 mt-1 ml-0">{option.description}</p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default PrefilledQuestionStep;
